import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://api.livestockify.com/api/admin";

export default function Coupons() {
  const navigate = useNavigate();

  const [coupons, setCoupons] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  /* 🔹 GET ALL COUPONS */
  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/coupons`);

      const normalized = res.data.map((c) => {
        const isExpiredByDate =
          c.expiryDate && new Date(c.expiryDate) < new Date();

        return {
          ...c,
          status: isExpiredByDate
            ? "Expired"
            : c.status === "ACTIVE"
            ? "Active"
            : c.status === "PAUSED"
            ? "Paused"
            : c.status === "EXPIRED"
            ? "Expired"
            : c.status,
        };
      });

      setCoupons(normalized);
    } catch (err) {
      console.error("Failed to fetch coupons", err);
    }
  };

  /* 🔍 SEARCH (FIXED) */
  const filteredCoupons = coupons.filter((c) =>
    (c.couponCode || "")
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  /* 🔄 SORT (FIXED) */
  const sortedCoupons = [...filteredCoupons].sort((a, b) => {
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();

    return sort === "newest" ? dateB - dateA : dateA - dateB;
  });

  const activeCoupons = sortedCoupons.filter((c) => c.status !== "Expired");
  const expiredCoupons = sortedCoupons.filter((c) => c.status === "Expired");

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <div className="flex-1 bg-white rounded-l-2xl p-4 sm:p-6 pl-14 sm:pl-6">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h1 className="text-xl font-semibold text-green-700">Coupons</h1>

          <div className="flex flex-wrap items-center gap-3">
            <input
              placeholder="Search by coupon code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border px-3 py-1.5 rounded-md text-sm w-full sm:w-auto"
            />

            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border px-3 py-1.5 rounded-md text-sm"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>

            <button
              onClick={() => navigate("/coupons/add")}
              className="border px-3 py-1.5 rounded-md text-sm"
            >
              + Add Coupon
            </button>
          </div>
        </div>

        {/* ACTIVE */}
        <h2 className="font-semibold mb-4">
          Active Coupons ({activeCoupons.length})
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {activeCoupons.map((c) => (
            <CouponCard key={c.id} coupon={c} refresh={fetchCoupons} />
          ))}
        </div>

        {/* EXPIRED */}
        <h2 className="font-semibold mb-4">
          Expired Coupons ({expiredCoupons.length})
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {expiredCoupons.map((c) => (
            <CouponCard key={c.id} coupon={c} refresh={fetchCoupons} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* 🔹 COUPON CARD */
function CouponCard({ coupon, refresh }) {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const statusColor =
    coupon.status === "Active"
      ? "bg-green-100 text-green-700"
      : coupon.status === "Paused"
      ? "bg-blue-100 text-blue-600"
      : "bg-gray-200 text-gray-600";

  const updateStatus = async (action) => {
    try {
      const urlMap = {
        PAUSE: "pause",
        RESUME: "resume",
        ACTIVATE: "resume",
      };

      await axios.put(
        `https://api.livestockify.com/api/admin/coupons/${coupon.id}/${urlMap[action]}`
      );

      refresh();
    } catch (err) {
      console.error("Status update failed", err);
    }
  };

  const deleteCoupon = async () => {
    try {
      await axios.delete(
        `https://api.livestockify.com/api/admin/coupons/${coupon.id}`
      );
      setShowDeleteConfirm(false);
      refresh();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  return (
    <div className="border rounded-xl p-4 shadow-sm">
      <div className="flex justify-between items-start mb-1">
        <h3 className="text-3xl font-bold text-green-600">
          {coupon.discount ??
            coupon.percentage ??
            coupon.discountPercent}
          % OFF
        </h3>

        <span className={`text-xs px-2 py-0.5 rounded ${statusColor}`}>
          {coupon.status}
        </span>
      </div>

      <p className="text-xs text-gray-500 mb-3">{coupon.description}</p>

      <div className="border border-dashed border-green-400 rounded-md py-2 text-center text-sm font-semibold text-green-700 mb-3 bg-green-50">
        {coupon.couponCode}
      </div>

      <p className="text-xs mb-1">
        Expiry Date:{" "}
        <span className="text-red-500 font-medium">
          {coupon.expiryDate}
        </span>
      </p>

      <p className="text-xs mb-3">
        Redemptions Left:{" "}
        <span className="font-semibold">
          {coupon.redemptionLimit}
        </span>
      </p>

      <div className="flex flex-wrap justify-between gap-2">
        {coupon.status === "Active" && (
          <button
            onClick={() => updateStatus("PAUSE")}
            className="text-xs px-3 py-1 rounded bg-blue-100 text-blue-600"
          >
            Pause
          </button>
        )}

        {coupon.status === "Paused" && (
          <button
            onClick={() => updateStatus("RESUME")}
            className="text-xs px-3 py-1 rounded bg-blue-100 text-blue-600"
          >
            Resume
          </button>
        )}

        {coupon.status === "Expired" && (
          <button
            onClick={() => updateStatus("ACTIVATE")}
            className="text-xs px-3 py-1 rounded bg-blue-100 text-blue-600"
          >
            Activate
          </button>
        )}

        <button
          onClick={() => navigate(`/coupons/edit/${coupon.id}`)}
          className="text-xs px-3 py-1 rounded bg-green-100 text-green-700"
        >
          Edit
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="text-xs px-3 py-1 rounded bg-red-100 text-red-600"
        >
          Delete
        </button>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-5 w-[320px]">
            <p className="text-sm mb-4">
              Are you sure you want to proceed with this deletion?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="border px-4 py-1 rounded"
              >
                Cancel
              </button>

              <button
                onClick={deleteCoupon}
                className="bg-red-500 text-white px-4 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
